import type {
  CashierPaymentMethod,
  CashierPaymentMethodGroup,
  SavedPaymentAccountIdAndFields,
} from "@/api/cashier";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import {
  setPaymentMethodList,
  setSelectedPaymentMethod,
  setSelectedPaymentMethodGroup,
  setSelectedSavedPaymentAccount,
} from "@/redux/slices/methodSlice";
import { useCallback } from "react";

export const useMethod = () => {
  const dispatch = useAppDispatch();

  const selectedPaymentMethod = useAppSelector(
    (state) => state.method.selectedPaymentMethod
  );
  const selectedPaymentMethodGroup = useAppSelector(
    (state) => state.method.selectedPaymentMethodGroup
  );
  const selectedSavedPaymentAccount = useAppSelector(
    (state) => state.method.selectedSavedPaymentAccount
  );

  const paymentMethodsOfSelectedAccount = useAppSelector(
    (state) => state.method.paymentMethodList
  );

  const isAGroupMethod = useCallback(
    (method: CashierPaymentMethod | CashierPaymentMethodGroup) => {
      return "payment_methods" in method;
    },
    []
  );

  const getFirstAvailablePaymentMethod = useCallback(
    (
      methodList:
        | (CashierPaymentMethod | CashierPaymentMethodGroup)[]
        | undefined
    ) => {
      if (!methodList) return;

      for (const method of methodList) {
        if (isAGroupMethod(method)) {
          const activeChild = method.payment_methods.find(
            (childMethod) => childMethod.status === "active"
          );
          if (activeChild) {
            return {
              method: activeChild,
              group: method,
            };
          }
        } else if (method?.status === "active") {
          return {
            method,
            group: undefined,
          };
        }
      }

      return {
        method: undefined,
        group: undefined,
      };
    },
    [isAGroupMethod]
  );

  const getPaymentMethodById = useCallback(
    (
      methodList:
        | (CashierPaymentMethod | CashierPaymentMethodGroup)[]
        | undefined,
      id: string
    ) => {
      if (!methodList) return;

      for (const method of methodList) {
        if (isAGroupMethod(method)) {
          const activeChild = method.payment_methods.find(
            (childMethod) => childMethod.id === id
          );
          if (activeChild) {
            return {
              method: activeChild,
              group: method,
            };
          }
        } else if (method?.id === id) {
          return {
            method,
            group: undefined,
          };
        }
      }

      return {
        method: undefined,
        group: undefined,
      };
    },
    [isAGroupMethod]
  );

  return {
    setSelectedPaymentMethod: useCallback(
      (paymentMethod: CashierPaymentMethod | undefined) => {
        dispatch(setSelectedPaymentMethod(paymentMethod));
      },
      [dispatch]
    ),
    setSelectedPaymentMethodGroup: useCallback(
      (paymentMethodGroup: CashierPaymentMethodGroup | undefined) => {
        dispatch(setSelectedPaymentMethodGroup(paymentMethodGroup));
      },
      [dispatch]
    ),
    setSelectedSavedPaymentAccount: useCallback(
      (paymentMethodGroup: SavedPaymentAccountIdAndFields | undefined) => {
        dispatch(setSelectedSavedPaymentAccount(paymentMethodGroup));
      },
      [dispatch]
    ),

    setPaymentMethodListOfTradingAccount: useCallback(
      (
        paymentMethodList:
          | (CashierPaymentMethod | CashierPaymentMethodGroup)[]
          | undefined
      ) => {
        dispatch(setPaymentMethodList(paymentMethodList));
      },
      [dispatch]
    ),

    selectedPaymentMethod,
    selectedPaymentMethodGroup,
    selectedSavedPaymentAccount,
    paymentMethodsOfSelectedAccount,

    isPaymentMethodIdSelected: useCallback(
      (id: string) => {
        return (
          selectedPaymentMethod?.id === id ||
          selectedPaymentMethodGroup?.id === id
        );
      },
      [selectedPaymentMethod, selectedPaymentMethodGroup]
    ),
    isPaymentGroupSelected: useCallback(
      (id: string) => {
        return selectedPaymentMethodGroup?.id === id;
      },
      [selectedPaymentMethodGroup]
    ),
    isAGroupMethod,
    getFirstAvailablePaymentMethod,
    getPaymentMethodById,
  };
};
